import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import {
  DollarSign, TrendingUp, Users, Wallet, Percent, ArrowUpRight, Info, RotateCcw,
} from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [txns, setTxns] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [series, setSeries] = useState([]);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("revenue");
  const [refundTxn, setRefundTxn] = useState(null);

  const loadRevenue = () => {
    api.get("/admin/revenue").then(({ data }) => setRevenue(data)).catch(() => {});
    api.get("/admin/payments/transactions").then(({ data }) => setTxns(data)).catch(() => setTxns([]));
    api.get("/admin/payouts/pending").then(({ data }) => setPayouts(data)).catch(() => setPayouts([]));
    api.get("/admin/payments/fee-series?days=14").then(({ data }) => setSeries(data.series || [])).catch(() => setSeries([]));
  };

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    loadRevenue();
  }, [user]);

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/home" replace />;

  const verify = async (uid, level) => {
    try {
      await api.post(`/admin/users/${uid}/verify?level=${level}`);
      setUsers((us) => us.map((u) => (u.user_id === uid ? { ...u, verification_level: level } : u)));
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs font-mono-label text-[color:hsl(var(--amber))]">Operator console</div>
            <h1 className="mt-1 font-display font-extrabold text-3xl">CLING Admin</h1>
          </div>
          <div className="flex gap-2 text-sm">
            {[
              ["revenue", "Revenue"],
              ["users", "Users"],
              ["ops", "Ops"],
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`admin-tab-${id}`}
                className={`rounded-full border px-3 py-1 text-xs transition ${tab === id ? "bg-[color:hsl(var(--teal))] text-white border-[color:hsl(var(--teal))]" : "hover:bg-secondary"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === "revenue" && (
          <RevenueBoard revenue={revenue} txns={txns} payouts={payouts} series={series} onRefund={setRefundTxn} />
        )}

        {tab === "users" && (
          <UsersTable users={users} verify={verify} />
        )}

        {tab === "ops" && stats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(stats).map(([k, v]) => (
              <div key={k} className="border rounded-2xl bg-card p-4" data-testid={`ops-stat-${k}`}>
                <div className="text-xs font-mono-label text-muted-foreground">{k.replace(/_/g, " ")}</div>
                <div className="mt-1 font-display font-bold text-2xl">{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <RefundDialog txn={refundTxn} onClose={() => setRefundTxn(null)} onDone={loadRevenue} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// REVENUE BOARD
// ---------------------------------------------------------------------------
function RevenueBoard({ revenue, txns, payouts, series, onRefund }) {
  if (!revenue) return <div className="mt-8 text-muted-foreground text-sm">Loading revenue…</div>;
  const k = revenue.kudos;
  const totalPayoutBacklog = payouts.reduce((a, p) => a + (p.pending || 0) + (p.available || 0), 0);
  const activePro = revenue.helper_pro.active;
  const pendingPro = revenue.helper_pro.pending_signups;

  return (
    <div className="mt-6 space-y-6" data-testid="revenue-board">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI
          label="Gross Kudos"
          icon={<DollarSign className="w-4 h-4" />}
          value={`$${k.monetary_gross_pending.toFixed(2)}`}
          sub={`${k.monetary_count} paid · ${k.free_count} free`}
          testId="kpi-gross"
        />
        <KPI
          label="Fee revenue"
          icon={<Percent className="w-4 h-4" />}
          value={`$${k.platform_fee_pending.toFixed(2)}`}
          sub={`blended ${(k.blended_fee_pct * 100).toFixed(1)}% · current ${(k.current_fee_pct * 100).toFixed(0)}%`}
          accent
          testId="kpi-fee"
        />
        <KPI
          label="Helper Pro subs"
          icon={<Users className="w-4 h-4" />}
          value={`${activePro}`}
          sub={`${pendingPro} pending`}
          testId="kpi-pro"
        />
        <KPI
          label="Payout backlog"
          icon={<Wallet className="w-4 h-4" />}
          value={`$${totalPayoutBacklog.toFixed(2)}`}
          sub={`${payouts.length} helpers`}
          testId="kpi-payouts"
        />
      </div>

      {/* Blended fee sparkline */}
      <div className="border rounded-2xl bg-card p-5" data-testid="fee-sparkline-panel">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2 text-sm font-mono-label text-[color:hsl(var(--teal))]">
              <TrendingUp className="w-4 h-4" /> Blended fee &amp; gross · last 14 days
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Fee % is computed from pinned per-Kudos values, so past changes to the admin fee never distort history.
            </p>
          </div>
          <SparklineLegend series={series} />
        </div>
        <div className="mt-4">
          <Sparkline series={series} />
        </div>
      </div>

      {/* Recent transactions */}
      <div className="border rounded-2xl bg-card overflow-hidden" data-testid="recent-txns-panel">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="text-sm font-mono-label text-muted-foreground">Recent payment transactions</div>
          <div className="text-xs text-muted-foreground">{txns.length} shown</div>
        </div>
        {txns.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No transactions yet. Send a Kudos to see this fill up.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs font-mono-label">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Kind</th>
                  <th className="text-right p-3">Amount</th>
                  <th className="text-right p-3">Refunded</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Session</th>
                  <th className="text-right p-3 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => {
                  const refunded = t.refunded_amount || 0;
                  const refundable = t.payment_status === "paid" || t.payment_status === "partially_refunded";
                  return (
                    <tr key={t.txn_id} className="border-t" data-testid={`txn-row-${t.txn_id}`}>
                      <td className="p-3 whitespace-nowrap text-muted-foreground">{fmtDate(t.created_at)}</td>
                      <td className="p-3"><KindBadge kind={t.kind} /></td>
                      <td className="p-3 text-right font-mono">${t.amount.toFixed(2)}</td>
                      <td className={`p-3 text-right font-mono ${refunded > 0 ? "text-orange-600 dark:text-orange-400" : "text-muted-foreground"}`}>
                        {refunded > 0 ? `–$${refunded.toFixed(2)}` : "—"}
                      </td>
                      <td className="p-3"><StatusBadge status={t.payment_status} /></td>
                      <td className="p-3 text-[11px] text-muted-foreground truncate max-w-[180px]">{t.stripe_session_id}</td>
                      <td className="p-3 pr-4 text-right">
                        {refundable ? (
                          <button
                            onClick={() => onRefund(t)}
                            data-testid={`refund-btn-${t.txn_id}`}
                            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] hover:border-[color:hsl(var(--amber))] hover:text-[color:hsl(var(--amber))] transition-colors"
                          >
                            <RotateCcw className="w-3 h-3" /> Refund
                          </button>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payout backlog */}
      <div className="border rounded-2xl bg-card overflow-hidden" data-testid="payout-backlog-panel">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="text-sm font-mono-label text-muted-foreground">Payout backlog — helpers with Stripe balance</div>
          <div className="text-xs text-muted-foreground">{payouts.length} helpers</div>
        </div>
        {payouts.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Info className="w-4 h-4" />
            No helper has a pending balance yet — this fills up as Kudos and Immediate Help sessions settle.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs font-mono-label">
              <tr>
                <th className="text-left p-3">Helper</th>
                <th className="text-right p-3">Pending</th>
                <th className="text-right p-3">Available</th>
                <th className="text-left p-3 pl-4">Currency</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.user_id} className="border-t" data-testid={`payout-row-${p.user_id}`}>
                  <td className="p-3">
                    <a href={`/u/${p.user_id}`} className="hover:underline flex items-center gap-1">
                      {p.name} <ArrowUpRight className="w-3 h-3 text-muted-foreground" />
                    </a>
                  </td>
                  <td className="p-3 text-right font-mono">${p.pending.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono">${p.available.toFixed(2)}</td>
                  <td className="p-3 pl-4 uppercase text-muted-foreground">{p.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// USERS TABLE (unchanged behaviour, moved into a component)
// ---------------------------------------------------------------------------
function UsersTable({ users, verify }) {
  return (
    <div className="mt-6 border rounded-2xl bg-card overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-secondary text-xs font-mono-label">
          <tr>
            <th className="text-left p-3">Name</th>
            <th className="text-left p-3">Email</th>
            <th className="text-left p-3">Role</th>
            <th className="text-left p-3">Verification</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} className="border-t" data-testid={`admin-user-${u.user_id}`}>
              <td className="p-3">{u.name}</td>
              <td className="p-3 text-muted-foreground">{u.email}</td>
              <td className="p-3">{u.role}</td>
              <td className="p-3">
                <Select value={u.verification_level} onValueChange={(v) => verify(u.user_id, v)}>
                  <SelectTrigger className="h-8 w-[180px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["none", "community", "experience", "credential", "expert"].map((l) => (
                      <SelectItem key={l} value={l}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UI ATOMS
// ---------------------------------------------------------------------------
function KPI({ label, value, sub, icon, accent, testId }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${accent ? "border-[color:hsl(var(--amber))]/40 bg-[color:hsl(var(--amber))]/5" : "bg-card"}`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs font-mono-label text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className={`mt-1.5 font-display font-extrabold text-2xl ${accent ? "text-[color:hsl(var(--amber))]" : ""}`}>{value}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>
    </div>
  );
}

function KindBadge({ kind }) {
  const map = {
    kudos: { label: "Kudos", cls: "bg-[color:hsl(var(--teal))]/15 text-[color:hsl(var(--teal))]" },
    helper_pro: { label: "Helper Pro", cls: "bg-[color:hsl(var(--amber))]/15 text-[color:hsl(var(--amber))]" },
    immediate_help: { label: "Immediate", cls: "bg-purple-500/15 text-purple-600 dark:text-purple-400" },
  };
  const m = map[kind] || { label: kind, cls: "bg-secondary text-muted-foreground" };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-mono-label ${m.cls}`}>{m.label}</span>;
}

function StatusBadge({ status }) {
  const map = {
    paid: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    authorized: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
    pending: "bg-muted text-muted-foreground",
    cancelled: "bg-secondary text-muted-foreground",
    failed: "bg-red-500/15 text-red-600 dark:text-red-400",
    refunded: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
    partially_refunded: "bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/30",
  };
  const label = status === "partially_refunded" ? "partial refund" : status;
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-mono-label ${map[status] || map.pending}`}>{label}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ---------------------------------------------------------------------------
// SPARKLINE — pure SVG, two-series (gross + blended fee %)
// ---------------------------------------------------------------------------
function Sparkline({ series }) {
  if (!series || series.length === 0) {
    return <div className="text-sm text-muted-foreground py-8 text-center">No Kudos activity in the last 14 days.</div>;
  }
  const w = 720, h = 120, pad = 20;
  const xs = series.map((_, i) => pad + (i / Math.max(1, series.length - 1)) * (w - pad * 2));
  const grossMax = Math.max(1, ...series.map((s) => s.gross));
  const feeMax = Math.max(0.01, ...series.map((s) => s.blended_fee_pct));
  const grossY = (v) => h - pad - (v / grossMax) * (h - pad * 2);
  const feeY = (v) => h - pad - (v / feeMax) * (h - pad * 2);
  const pathGross = series.map((s, i) => `${i === 0 ? "M" : "L"}${xs[i]},${grossY(s.gross)}`).join(" ");
  const pathFee = series.map((s, i) => `${i === 0 ? "M" : "L"}${xs[i]},${feeY(s.blended_fee_pct)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32" data-testid="fee-sparkline-svg">
      <path d={pathGross} fill="none" stroke="hsl(var(--teal))" strokeWidth="2" />
      <path d={pathFee} fill="none" stroke="hsl(var(--amber))" strokeWidth="2" strokeDasharray="4 3" />
      {series.map((s, i) => (
        <g key={s.date}>
          <circle cx={xs[i]} cy={grossY(s.gross)} r="2.5" fill="hsl(var(--teal))" />
          <circle cx={xs[i]} cy={feeY(s.blended_fee_pct)} r="2" fill="hsl(var(--amber))" />
        </g>
      ))}
    </svg>
  );
}

function SparklineLegend({ series }) {
  const lastGross = series.length ? series[series.length - 1].gross : 0;
  const lastFee = series.length ? series[series.length - 1].blended_fee_pct : 0;
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-0.5 bg-[color:hsl(var(--teal))] rounded-full" />
        <span className="text-muted-foreground">Gross</span>
        <b>${lastGross.toFixed(0)}</b>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-0.5 bg-[color:hsl(var(--amber))] rounded-full" />
        <span className="text-muted-foreground">Blended fee</span>
        <b>{(lastFee * 100).toFixed(1)}%</b>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// REFUND DIALOG — one-click full refund or edit amount for partial
// ---------------------------------------------------------------------------
function RefundDialog({ txn, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("requested_by_customer");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (txn) {
      const remaining = Math.max(0, (txn.amount || 0) - (txn.refunded_amount || 0));
      setAmount(remaining.toFixed(2));
      setReason("requested_by_customer");
      setNote("");
    }
  }, [txn]);

  if (!txn) return null;
  const remaining = Math.max(0, (txn.amount || 0) - (txn.refunded_amount || 0));
  const amountNum = parseFloat(amount || "0") || 0;
  const isFull = Math.abs(amountNum - remaining) < 0.005;
  const invalid = amountNum <= 0 || amountNum > remaining + 0.005;

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        amount: isFull ? null : amountNum,
        reason: note ? note : reason,
      };
      const { data } = await api.post(`/admin/payments/${txn.txn_id}/refund`, body);
      toast.success(
        data.fully_refunded
          ? `Full refund of $${data.total_refunded.toFixed(2)} sent to buyer`
          : `Partial refund of $${data.refunded_amount.toFixed(2)} sent (total refunded $${data.total_refunded.toFixed(2)})`
      );
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!txn} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-[color:hsl(var(--amber))]" /> Refund transaction
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-xl border p-3 bg-secondary/40 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Kind</span><KindBadge kind={txn.kind} /></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Original amount</span><b>${txn.amount.toFixed(2)}</b></div>
            {txn.refunded_amount > 0 && (
              <div className="flex justify-between text-orange-600 dark:text-orange-400">
                <span>Already refunded</span><b>–${txn.refunded_amount.toFixed(2)}</b>
              </div>
            )}
            <div className="flex justify-between pt-1 border-t"><span className="text-muted-foreground">Refundable now</span><b>${remaining.toFixed(2)}</b></div>
          </div>

          <div>
            <label className="text-xs font-mono-label text-muted-foreground">Refund amount (USD)</label>
            <div className="mt-1 flex gap-2">
              <Input
                type="number"
                min="0.01"
                step="0.01"
                max={remaining}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={invalid ? "border-red-500" : ""}
                data-testid="refund-amount-input"
              />
              <Button
                variant="outline"
                onClick={() => setAmount(remaining.toFixed(2))}
                className="whitespace-nowrap rounded-full text-xs"
                data-testid="refund-full-btn"
              >
                Full refund
              </Button>
            </div>
            {invalid && amount && (
              <div className="mt-1 text-[11px] text-red-500">Enter a value between $0.01 and ${remaining.toFixed(2)}</div>
            )}
          </div>

          <div>
            <label className="text-xs font-mono-label text-muted-foreground">Reason</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1" data-testid="refund-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="requested_by_customer">Requested by customer</SelectItem>
                <SelectItem value="duplicate">Duplicate payment</SelectItem>
                <SelectItem value="fraudulent">Fraudulent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-mono-label text-muted-foreground">Internal note (optional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why is this being refunded? (visible only to admins)"
              className="mt-1 text-sm"
              rows={2}
              data-testid="refund-note-input"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            className="btn-amber"
            onClick={submit}
            disabled={busy || invalid}
            data-testid="refund-submit-btn"
          >
            {busy ? "Processing…" : isFull ? `Refund $${amountNum.toFixed(2)}` : `Refund $${amountNum.toFixed(2)} (partial)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
